import { Injectable, signal } from '@angular/core';

export type Theme = 'light' | 'dark' | 'system';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly currentThemeSignal = signal<Theme>(this.loadTheme());
  readonly currentTheme = this.currentThemeSignal.asReadonly();

  private readonly appliedThemeSignal = signal<string>(this.resolveTheme(this.loadTheme()));
  readonly appliedTheme = this.appliedThemeSignal.asReadonly();

  private systemThemeChangeHandler: ((e: MediaQueryListEvent) => void) | null = null;
  private syncFn: ((theme: Theme) => void) | null = null;

  constructor() {
    this.applyTheme(this.currentThemeSignal());
    this.listenForStorageChanges();
  }

  /**
   * Register a callback that fires whenever the theme changes.
   * Used by the cabinet app to persist the theme to the backend
   * so that the editor (running on a different origin) stays in sync.
   */
  registerSync(fn: (theme: Theme) => void): void {
    this.syncFn = fn;
  }

  setTheme(theme: Theme): void {
    this.currentThemeSignal.set(theme);
    localStorage.setItem('currentTheme', theme);
    this.applyTheme(theme);
    this.syncFn?.(theme);
  }

  initializeTheme(): void {
    this.applyTheme(this.currentThemeSignal());
  }

  getCurrentTheme(): Theme {
    return this.currentThemeSignal();
  }

  getAppliedTheme(): string {
    return this.appliedThemeSignal();
  }

  private loadTheme(): Theme {
    return (localStorage.getItem('currentTheme') as Theme) || 'system';
  }

  private resolveTheme(theme: Theme): 'light' | 'dark' {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
  }

  private applyTheme(theme: Theme): void {
    const resolved = this.resolveTheme(theme);
    this.appliedThemeSignal.set(resolved);
    this.setDocumentTheme(resolved);

    if (theme === 'system') {
      this.listenForSystemThemeChanges();
    }
  }

  // Propagate theme changes made in another window/WebContentsView of the
  // same origin (e.g. cabinet → open editor tabs on desktop). The `storage`
  // event only fires in OTHER documents, so the originating window is unaffected.
  private listenForStorageChanges(): void {
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key !== 'currentTheme' || e.newValue === null) return;
      const theme = e.newValue as Theme;
      if (theme === this.currentThemeSignal()) return;
      this.currentThemeSignal.set(theme);
      this.applyTheme(theme);
    });
  }

  private listenForSystemThemeChanges(): void {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    if (this.systemThemeChangeHandler) {
      mediaQuery.removeEventListener('change', this.systemThemeChangeHandler);
    }

    this.systemThemeChangeHandler = (e: MediaQueryListEvent): void => {
      if (this.currentThemeSignal() === 'system') {
        const newTheme = e.matches ? 'dark' : 'light';
        this.setDocumentTheme(newTheme);
        this.appliedThemeSignal.set(newTheme);
      }
    };

    mediaQuery.addEventListener('change', this.systemThemeChangeHandler);
  }

  /** Marks <html> with the theme and its colour scheme, so native controls and scrollbars match it. */
  private setDocumentTheme(resolved: 'light' | 'dark'): void {
    const root = document.querySelector('html');
    root?.setAttribute('data-theme', resolved);
    if (root?.style) root.style.colorScheme = resolved;
  }
}
